import { createSelector } from "@reduxjs/toolkit";
import { applyPersonalization } from "../../Utility/personalizationEngine";

// Stable fallback references — returning a fresh [] on every call (state?.x || [])
// defeats createSelector's reference-equality memoization until the real data
// loads, forcing a recompute on every dispatch during the loading race instead
// of just when the underlying slice actually changes.
const EMPTY_ARRAY = [];

const selectRawSectionMasters = (state) => state?.homePageSliceReducer?.homePageData?.sectionMasters || EMPTY_ARRAY;
const selectRawSectionContentMasters = (state) =>
  state?.homePageSliceReducer?.homePageData?.sectionContentMasters || EMPTY_ARRAY;
const selectGenericRules = (state) => state?.personalizationSliceReducer?.generic?.rules || EMPTY_ARRAY;
const selectMemberRules = (state) => state?.personalizationSliceReducer?.member?.rules || EMPTY_ARRAY;
const selectCurrentEncryptedMemberIdentifier = (state) => state?.validateLoginTokenSliceReducer?.data?.encryptedMemberIdentifier || null;

// A rule author only ever has the encryptedMemberIdentifier to hand — it's the only
// identifier surfaced anywhere in the admin workflow (upload field, filename, search;
// see Frontend/src/Pages/PersonalizationFile) — and it may be typed in either raw or
// percent-encoded form, same ambiguity as the admin's search box. decodeURIComponent
// throws on a string that isn't actually percent-encoded content, so this only decodes
// when the value contains a literal "%"; anything else (including the plain, unencoded
// identifier) passes through unchanged. A decode failure falls back to the raw value
// rather than making the comparison throw.
const normalizeIdentifier = (value) => {
  if (!value || !String(value).includes("%")) return value;
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

// Memoized by reference equality of its inputs — recomputes only when the
// raw Home Page data or the rule set actually changes, not on every render
// of every section. Generic rules apply to everyone, unfiltered. Member
// rules are already scoped to the current member by which file got fetched
// (one file per member, named by encryptedMemberIdentifier — see
// personalizationApi.js), but the rule-level memberIdentifier check here
// stays as a harmless safety net in case a member's file ever carries a rule
// meant for someone else. Compared against encryptedMemberIdentifier (raw or
// percent-encoded, normalized before comparing) rather than the plain
// memberIdentifier — this field is independent of which S3 key was fetched,
// but the plain identifier isn't something a rule author would ever have.
// Generic rules go first as the baseline, member rules layer on top,
// matching the merge order the engine expects.
export const selectPersonalizedHomeData = createSelector(
  [selectRawSectionMasters, selectRawSectionContentMasters, selectGenericRules, selectMemberRules, selectCurrentEncryptedMemberIdentifier],
  (sectionMasters, sectionContentMasters, genericRules, memberRules, currentEncryptedMemberIdentifier) => {
    const normalizedCurrentIdentifier = normalizeIdentifier(currentEncryptedMemberIdentifier);
    const applicableMemberRules = memberRules.filter(
      (rule) =>
        !rule?.memberIdentifier ||
        String(normalizeIdentifier(rule.memberIdentifier)) === String(normalizedCurrentIdentifier)
    );
    const rules = [...genericRules, ...applicableMemberRules];

    if (!rules.length) {
      return { sectionMasters, sectionContentMasters, sectionOverrides: {} };
    }
    return applyPersonalization({ sectionMasters, sectionContentMasters }, rules);
  }
);
